'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useModuleViewedReport } from 'components/analytics/hooks/useModuleViewedReport';
import { cn } from 'utils/styles';
import ToastBanner from './ToastBanner';
import ToastCard from './ToastCard';
import ToastCollapsibleCard from './ToastCollapsibleCard';
import { useDisclosureBannerOverlap } from './useDisclosureBannerOverlap';
import { useToastTrigger } from './useToastTrigger';
import { getToastDismissedKey, TOAST_VARIANT_BY_LABEL } from './consts';
import type { ToastProps, ToastVariant } from './types';

/** Scroll-triggered toast anchored to the bottom of the viewport. */
const Toast = ({
  variant = 'dismissable',
  dismissId,
  reshowOnPageReload,
  sys,
  scrollDepthPercent,
  timeOnSiteSeconds,
  category,
  headline,
  media,
  cta,
  ctaAsButton,
  className,
}: ToastProps) => {
  const resolvedVariant = TOAST_VARIANT_BY_LABEL[variant];
  const [readKey, setReadKey] = useState<string | null>(null);
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const [focusWithin, setFocusWithin] = useState(false);
  const revealed = useToastTrigger({ scrollDepthPercent, timeOnSiteSeconds });
  const bannerOverlap = useDisclosureBannerOverlap();
  const storageKey = getToastDismissedKey(dismissId ?? sys?.id ?? 'default');
  const rootRef = useRef<HTMLDivElement>(null);
  const focusFromPointer = useRef(false);
  const impressionKey = useRef<string | null>(null);
  const reportViewed = useModuleViewedReport();

  const suppressed = readKey !== storageKey || dismissedKey === storageKey;
  const visible = revealed || focusWithin;

  useEffect(() => {
    setFocusWithin(false);

    let stored: string | null = null;
    if (!reshowOnPageReload) {
      try {
        stored = sessionStorage.getItem(storageKey);
      } catch {
        // Blocked storage must not block the toast.
      }
    }
    if (stored) setDismissedKey(storageKey);
    setReadKey(storageKey);
  }, [storageKey, reshowOnPageReload]);

  useEffect(() => {
    if (!revealed || suppressed || impressionKey.current === storageKey) return;

    impressionKey.current = storageKey;
    reportViewed(rootRef.current);
  }, [revealed, suppressed, storageKey, reportViewed]);

  const handleDismiss = () => {
    setDismissedKey(storageKey);
    if (reshowOnPageReload) return;

    try {
      sessionStorage.setItem(storageKey, '1');
    } catch {
      // The in-memory dismissal still works when storage is blocked.
    }
  };

  if (suppressed) return null;

  const cardProps = { category, headline, media, cta, ctaAsButton, className };

  // Keyed by an enum so the annotation enforces exhaustiveness; dropping it would let a
  // new enum member go unhandled silently.
  // oxlint-disable-next-line anti-slop/no-known-value-widening
  const surfaces: Record<ToastVariant, ReactNode> = {
    dismissable: <ToastCard {...cardProps} onDismiss={handleDismiss} />,
    collapsible: <ToastCollapsibleCard {...cardProps} />,
    banner: <ToastBanner headline={headline} cta={cta} className={className} onDismiss={handleDismiss} />,
  };

  return (
    <div
      ref={rootRef}
      className={cn(
        // `fixed!` because `[data-rc-extend] > :last-child { position: relative }`
        // in app/globals.css overrides Tailwind's layered `.fixed` utility.
        'fixed! bottom-2 z-25 sm:bottom-10',
        resolvedVariant === 'banner' ? 'inset-x-2 sm:inset-x-10' : 'right-2 max-sm:left-2 sm:right-10',
        'transition-transform duration-300 ease-in-out motion-reduce:transition-none',
        !visible && 'pointer-events-none translate-y-[calc(100%+4rem)]',
      )}
      style={visible && bannerOverlap > 0 ? { transform: `translateY(-${bannerOverlap}px)` } : undefined}
      role="region"
      aria-label={category?.trim() || 'Notification'}
      inert={!visible}
      onPointerDown={() => {
        focusFromPointer.current = true;
      }}
      onFocus={() => {
        if (!focusFromPointer.current) setFocusWithin(true);
        focusFromPointer.current = false;
      }}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocusWithin(false);
      }}
      data-toast>
      {surfaces[resolvedVariant]}
    </div>
  );
};

export default Toast;
